import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Nueva contraseña — Mostrando México" },
      { name: "description", content: "Crea una nueva contraseña para tu cuenta de Mostrando México." },
      { property: "og:title", content: "Nueva contraseña — Mostrando México" },
      { property: "og:description", content: "Crea una nueva contraseña para tu cuenta de Mostrando México." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === "PASSWORD_RECOVERY" || session) setReady(true);
      });
      unsub = () => data.subscription.unsubscribe();
      const { data: s } = await supabase.auth.getSession();
      if (s.session) setReady(true);
    })();
    return () => unsub?.();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (password.length < 8) return setMsg("La contraseña debe tener al menos 8 caracteres.");
    if (password !== confirm) return setMsg("Las contraseñas no coinciden.");
    setLoading(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
    } catch (e: any) {
      setMsg(e?.message ?? "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  }

  const input =
    "mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm outline-none focus:border-primary";

  return (
    <main className="mx-auto max-w-md px-4 py-16">
      <h1 className="text-2xl font-semibold text-foreground">Crea una nueva contraseña</h1>
      {done ? (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-muted-foreground">Listo, tu contraseña se cambió.</p>
          <a href="/mi-cuenta" className="inline-flex h-11 items-center rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground">
            Ir a mi cuenta
          </a>
        </div>
      ) : !ready ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Abre esta página desde el enlace que te enviamos por correo. Si el enlace venció, pide uno nuevo en{" "}
          <a href="/auth" className="text-primary underline">Iniciar sesión</a>.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nueva contraseña</span>
            <input type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Confirmar contraseña</span>
            <input type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} />
          </label>
          {msg && <p className="text-sm text-destructive">{msg}</p>}
          <button type="submit" disabled={loading} className="inline-flex h-11 w-full items-center justify-center rounded-xl bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-60">
            {loading ? "..." : "Guardar contraseña"}
          </button>
        </form>
      )}
    </main>
  );
}
